import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import ModalIndex from "@Modals/layout/index";
import DateRangePickerDropdown from "@components/dateRange/DropdownDateRange";
import FormTextarea from "@components/formGroup/formTextarea";
import InputWithAddOptions from "@components/input/InputWithAddOptions";
import { checkEnterpriseId } from "@helpers/_functions";
import { ListboxSimple, Input, Switch } from "@components/tailwindComponents";
import { useForm, Controller } from "react-hook-form";
import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker";
import InputFileMultipleArh from "@components/input/InputFileMultiple";
import { REGEX_VALID_FILES } from "@helpers/_constants";
import useDisciplinary from "./helpers/_functions";
import { useMultipleFileUpload } from "@hooks/useMultipleFileUpload";
import { useDispatch } from "react-redux";
import { disciplinaryEnterpriseActions } from "@store/actions";
import { setDisciplinaryCategory } from "@store/actions/disciplinary/_disciplinaryCategoryAction";
import { setDisciplinaryGradation } from "@store/actions/disciplinary/_disciplinaryGradationAction";
import { useNotification } from "@hooks/useNotification";

const ModalAddDisciplinary = (props) => {
  const {
    reset,
    control,
    handleSubmit,
    resetField,
    getValues,
    clearErrors
  } = useForm({
    defaultValues: {
      title: "",
      category: null,
      gradation: null,
      typeEvent: null,
      periodSelected: [],
      alertDate: null,
      notes: "",
      files: [],
    }
  });
  const { employeeId, disciplinary } = props;
  const { t: translator } = useTranslation("disciplinary.disciplinary");
  const [isLoading, setIsLoading] = useState(false);
  const { showError } = useNotification();
  const reduxDispatch = useDispatch();
  const {
    disciplinaryCategoryList,
    addDisciplinaryCategory,
    deleteDisciplinaryCategory,
    disciplinaryGradationList,
    addDisciplinaryGradation,
    deleteDisciplinaryGradation,
    upsertDisciplinary,
    handleFormErrors
  } = useDisciplinary();

  const refreshDisciplinaryInApp = () => {
    reduxDispatch(disciplinaryEnterpriseActions.refreshData);
  };

  const {
    oldFiles,
    handleDeleteOldFiles,
    removeOldFiles,
    toDeleteOldFiles,
    allFileDone,
    resetData,
    handleUploadStatusChange,
    resetInputFiles,
  } = useMultipleFileUpload({
    defaultFiles: disciplinary?.files,
    onDeleteOldFile: refreshDisciplinaryInApp,
  });
  const [isNotificationEnabled, setIsNotificationEnabled] = useState(true);

  useEffect(() => {
    if (!props.isOpen) {
      reset({
        title: "",
        category: null,
        gradation: null,
        typeEvent: null,
        periodSelected: [],
        alertDate: null,
        notes: "",
        files: [],
      });
      clearErrors();
      setIsLoading(false);
      removeOldFiles();
      resetData();
      return;
    }

    if (disciplinary?._id) {
      const periodSelected = disciplinary.startDate
        ? [
          {
            startDate: moment(disciplinary.startDate).toDate(),
            endDate: moment(disciplinary.endDate).toDate(),
          },
        ]
        : [];

      reset({
        title: disciplinary.title || "",
        category: disciplinary.category || null,
        gradation: disciplinary.gradation || null,
        typeEvent:
          typeEventOptions.find((option) => option._id === disciplinary.typeEvent) ||
          null,
        periodSelected,
        alertDate: disciplinary.alertDate
          ? moment(disciplinary.alertDate).toDate()
          : null,
        notes: disciplinary.notes || "",
        files: disciplinary.files || [],
      });

      resetInputFiles();
    }
  }, [props.isOpen, disciplinary]);


  const typeEventOptions = useMemo(() => {
    return [
      {
        _id: "Event",
        name: translator("Événement"),
      },
      {
        _id: "Communication",
        name: translator("Communication"),
      },
      {
        _id: "Sanction",
        name: translator("Sanction"),
      },
    ];
  }, [translator]);

  const onSaveClicked = async (data) => {
    try {
      setIsLoading(true);
      const { startDate, endDate } = data.periodSelected?.[0] || {};
      const disciplinaryToSave = {
        ...data,
        startDate: moment(startDate).format("YYYY-MM-DD"),
        endDate: moment(endDate).format("YYYY-MM-DD"),
        alertDate: data?.alertDate ? moment(data.alertDate).format("YYYY-MM-DD") : null,
        typeEvent: data.typeEvent?._id,
        category: data.category?._id ? data.category._id : data.category || null,
        gradation: data.gradation?._id ? data.gradation._id : data.gradation || null,
        Enterprise: checkEnterpriseId(),
        Employer: employeeId,
      };
      if (toDeleteOldFiles?.length) {
        await removeOldFiles();
      }
      if (disciplinary?._id) {
        disciplinaryToSave._id = disciplinary?._id;
      }
      const onSuccess = (_) => {
        props.closeModal();
      };
      await upsertDisciplinary(disciplinaryToSave, onSuccess, isNotificationEnabled);
    } catch (error) {
      console.log(error);
    }

    setIsLoading(false);
  };

  const handleChangeNotification = async (checked) => {
    setIsNotificationEnabled(checked);
  };

  const handleAddItemDisciplinaryCategory = (data) => {
    if (disciplinaryCategoryList.find((item) => item.name === data.name)) {
      showError("already_exists", { object: "categoryUpper" });
    } else {
      addDisciplinaryCategory({
        ...data,
        entreprise: checkEnterpriseId(data.entreprise)
      });
      const { Enterprise, ...essentialData } = data;
      reduxDispatch(
        setDisciplinaryCategory({
          list: [...disciplinaryCategoryList, essentialData],
          isLoaded: true
        })
      );
    }
  }

  const handleDeleteItemDisciplinaryCategory = async (data) => {
    const isDeleted = await deleteDisciplinaryCategory(data);
    if (!isDeleted) return;
    reduxDispatch(
      setDisciplinaryCategory({
        list: disciplinaryCategoryList.filter((item) => item.name !== data.name),
        isLoaded: true
      })
    );
  }

  const handleAddItemDisciplinaryGradation = (data) => {
    if (disciplinaryGradationList.find((item) => item.name === data.name)) {
      showError("already_exists", { object: "gradationUpper" });
    } else {
      addDisciplinaryGradation({
        ...data,
        entreprise: checkEnterpriseId()
      });
      const { Enterprise, ...essentialData } = data;
      reduxDispatch(
        setDisciplinaryGradation({
          list: [...disciplinaryGradationList, essentialData],
          isLoaded: true
        })
      );
    }
  }

  const handleDeleteItemDisciplinaryGradation = async (data) => {
    const isDeleted = await deleteDisciplinaryGradation(data);
    if (!isDeleted) return;

    reduxDispatch(
      setDisciplinaryGradation({
        list: disciplinaryGradationList.filter(
          (item) => item.name !== data.name
        ),
        isLoaded: true
      })
    );
  }

  return (
    <ModalIndex
      isOpen={props.isOpen}
      title={disciplinary?._id ? translator("Modifier événement") : translator("Ajouter un événement")}
      mustWaiting={!allFileDone}
      onClickClose={props.closeModal}
      isLoading={isLoading}
      widthType="large"
      buttonOptions={{
        click: handleSubmit(onSaveClicked, handleFormErrors),
        color: "green",
        title: translator("Enregistrer")
      }}
      buttonCancelOptions={{
        click: props.closeModal,
        color: "outlined",
        title: translator("Annuler")
      }}
    >
      <div className="arh-w-full arh-flex arh-flex-col arh-px-8 arh-mt-8">
        <div className="arh-flex arh-flex-row arh-justify-between arh-gap-6">
          <div className="arh-flex arh-flex-col arh-w-1/2">
            <div className="arh-w-full arh-mb-2">
              <Controller
                control={control}
                name="title"
                rules={{ required: true }}
                render={({
                  field: { onChange, value, name },
                  fieldState: { invalid },
                }) => (
                  <Input
                    label={translator("Titre de l'événement")}
                    placeholder={translator("Titre")}
                    value={value}
                    onChange={onChange}
                    name={name}
                    invalid={invalid}
                  />
                )}
              />
            </div>
            <div className="arh-w-full arh-mb-2">
              <Controller
                control={control}
                name="category"
                render={({
                  field: { onChange, value, name },
                  fieldState: { invalid },
                }) => (
                  <>
                    <label
                      className={`arh-mb-1 arh-w-full arh-font-poppins arh-text-left arh-text-xs arh-font-medium ${invalid
                        ? "arh-text-invalidColorTextInput"
                        : "arh-text-darkColor"
                        }  `}
                    >
                      {translator("Catégorie")}
                    </label>
                    <InputWithAddOptions
                      defaultText={translator("Catégorie")}
                      defaultSelected={value?._id}
                      optionsList={disciplinaryCategoryList || []}
                      secondDefaultText={translator("Ajouter")}
                      name={name}
                      value={value?.name}
                      optionListLabel="name"
                      onChange={(e) => onChange(e.target.dataset.id || null)}
                      addItem={handleAddItemDisciplinaryCategory}
                      deleteItem={handleDeleteItemDisciplinaryCategory}
                      validateDropdown={invalid}
                      showAction={true}
                      hideEdit={true}
                    />
                  </>
                )}
              />
            </div>

            <div className="arh-w-full arh-mb-2">
              <Controller
                control={control}
                name="files"
                defaultValue={[]}
                render={({
                  field: { onChange, value },
                  fieldState: { invalid },
                }) => (
                  <>
                    <label
                      className={`arh-mb-1 arh-w-full arh-font-poppins arh-text-left arh-text-xs arh-font-medium ${invalid
                        ? "arh-text-invalidColorTextInput"
                        : "arh-text-darkColor"
                        }  `}
                    >
                      {translator("Charger un fichier")}
                    </label>
                    <div className="input_file_theme_employer">
                      <InputFileMultipleArh
                        handleChangeStatus={handleUploadStatusChange(value, onChange)}
                        onDeleteOldFile={handleDeleteOldFiles}
                        classUploadFile="employer_theme_upload GreenThemeSelect arh-custom-fileUpload-disciplinary arh-border-greyBorderInput arh-border arh-border-solid arh-rounded arh-p-[0.5rem] "
                        regexp={REGEX_VALID_FILES}
                        hideTitle={true}
                        allFile={true}
                        dataFile={oldFiles}
                        isValid={invalid}
                      />
                    </div>
                  </>
                )}
              />
            </div>
          </div>

          <div className="arh-flex arh-flex-col arh-w-1/2">
            <div className="arh-flex arh-items-center arh-justify-between arh-mb-2">
              <Controller
                control={control}
                name="typeEvent"
                defaultValue={null}
                rules={{ required: true }}
                render={({
                  field: { onChange, value, name },
                  fieldState: { invalid },
                }) => (
                  <ListboxSimple
                    placeholder={translator("Type d'événement")}
                    name={name}
                    label={translator("Type d'événement")}
                    optionList={typeEventOptions}
                    selectedValue={value}
                    setSelectedValue={onChange}
                    invalid={invalid}
                    noRadio={true}
                  />
                )}
              />
            </div>
            <div className="arh-w-full arh-mb-2">
              <Controller
                control={control}
                name="gradation"
                render={({
                  field: { onChange, value, name },
                  fieldState: { invalid },
                }) => (
                  <>
                    <label
                      className={`arh-mb-1 arh-w-full arh-font-poppins arh-text-left arh-text-xs arh-font-medium ${invalid
                        ? "arh-text-invalidColorTextInput"
                        : "arh-text-darkColor"
                        }  `}
                    >
                      {translator("Gradation")}
                    </label>
                    <InputWithAddOptions
                      defaultText={translator("Gradation")}
                      defaultSelected={value?._id}
                      optionsList={disciplinaryGradationList || []}
                      secondDefaultText={translator("Ajouter")}
                      name={name}
                      value={value?.name}
                      optionListLabel="name"
                      onChange={(e) => onChange(e.target.dataset.id || null)}
                      addItem={handleAddItemDisciplinaryGradation}
                      deleteItem={handleDeleteItemDisciplinaryGradation}
                      validateDropdown={invalid}
                      showAction={true}
                      hideEdit={true}
                    />
                  </>
                )}
              />
            </div>

            <div className="arh-w-full arh-mb-2">
              <Controller
                control={control}
                name="periodSelected"
                defaultValue={[]}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { invalid },
                }) => (
                  <>
                    <label
                      className={`arh-mb-1 arh-w-full arh-font-poppins arh-text-left arh-text-xs arh-font-medium ${invalid ? "arh-text-invalidColorTextInput" : "arh-text-darkColor"
                        }`}
                    >
                      {translator("Période")}
                    </label>
                    <div className="dropdown_content_hour items_filter datepicker_modals_parcours datePicker_posteDisciplinary blackSvgColor">
                      <div className="dateRange_green position_relative DateRangePicker_customisation DateRangePicker_customisation--green">
                        <DateRangePickerDropdown
                          key="date_range_periode"
                          classParent="arhDatePicker"
                          idInput="daterange"
                          className={
                            value?.[0]
                              ? "input_simple "
                              : "input_simple !arh-pl-[1.25rem] "
                          }
                          dateSelected={value?.[0]}
                          textInput={translator("Période")}
                          SaveCloseRange={onChange}
                          showResetFilter={
                            getValues("periodSelected")?.startDate &&
                              getValues("periodSelected")?.endDate
                              ? true
                              : false
                          }
                          onResetFilter={() => resetField("periodSelected")}
                          classSwitch="c-switch-pill-checked--green--color--to--left"
                          classBackground="green"
                          isInForeground={true}
                          marginTop={-10}
                          customClass="arh-bottom-[3.125rem] !arh-top-auto"
                          invalid={invalid}
                        />
                      </div>
                    </div>
                  </>
                )}
              />
            </div>

            <div className="arh-w-full arh-mb-2">
              <Controller
                control={control}
                name="alertDate"
                render={({
                  field: { onChange, value, name },
                  fieldState: { invalid },
                }) => (
                  <div
                    className="arh-w-full calendar_with_btn employerThemeDatePicker wadeDatePicker "
                  >
                    <label
                      className={`arh-mb-1 arh-w-full arh-font-poppins arh-text-left arh-text-xs arh-font-medium ${invalid ? "arh-text-invalidColorTextInput" : "arh-text-darkColor"}`}
                    >
                      {translator("Définir une alerte")}
                    </label>
                    <DefaultDatePicker
                      className="focus_green employe_depuis GreenThemeSelect arh-font-normal"
                      name={name}
                      dateValue={value}
                      placeholder={translator("Date")}
                      setDateValue={onChange}
                      showTime={false}
                      isStart={true}
                      invalid={invalid}
                    />
                  </div>
                )}
              />
            </div>
          </div>
        </div>

        <Controller
          control={control}
          name="notes"
          defaultValue=""
          render={({ field: { onChange, onBlur, value } }) => (
            <div className="arh-flex arh-flex-col arh-items-start arh-mt-6">
              <div className="arh-flex arh-flex-col arh-w-full">
                <div className="arh-mb-2">
                  <FormTextarea
                    id="notes"
                    name="notes"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    rows={10}
                    label={translator("Notes")}
                  />
                </div>
              </div>
            </div>
          )}
        />

        {/* Switch notification */}
        {!disciplinary?._id &&
          <div className="arh-w-full arh-flex arh-items-center">
            <Switch
              color="arh-bg-greenColor"
              handleChange={handleChangeNotification}
              checked={isNotificationEnabled}
            />
            <span className="arh-text-left arh-ml-4 arh-font-poppins arh-text-xs arh-font-normal arh-text-black">
              {translator("Envoyer une notification lors de la création")}
            </span>
          </div>
        }
      </div>
    </ModalIndex>
  );
};

export default ModalAddDisciplinary;
